import { memo, useCallback, useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";

import { FiltersList } from "app/pages/sales-intelligence/modules/advanced-search-filtering/components/FiltersList/FiltersList";
import { FilterPanelContentContainer } from "app/pages/sales-intelligence/modules/advanced-search-filtering/containers/FilterPanelContent/FilterPanelContentContainer";
import { useFiltersManager } from "app/pages/sales-intelligence/modules/advanced-search-filtering/hooks/useFiltersManager";
import { setIsFiltersPanelExpanded } from "app/pages/sales-intelligence/modules/advanced-search-filtering/store/action-creators";
import { selectIsFiltersPanelExpanded } from "app/pages/sales-intelligence/modules/advanced-search-filtering/store/selectors";
import { createAndroidFilterConfig } from "app/pages/sales-intelligence/modules/find-apps-filters/configs/createAndroidFilterConfig";
import withFindAppsTracking from "app/pages/sales-intelligence/modules/find-apps-filters/hoc/withFindAppsTracking";
import useUpdateTabState from "app/pages/sales-intelligence/modules/find-apps-filters/hooks/useUpdateTabState";
import useFindAppsDescriptionQuery from "app/pages/sales-intelligence/modules/find-apps-filters/queries/useGetAppsDescription";
import {
    selectFindAppsAndroidExpandedFilter,
    selectFindAppsAndroidFiltersValue,
} from "app/pages/sales-intelligence/store/find-apps/selectors";
import type { IAppsAndroidFiltersDto } from "app/pages/sales-intelligence/store/find-apps/types/androidFilters.dto";
import { AppsSourceType } from "app/pages/sales-intelligence/store/find-apps/types/appsSourceType";

const WrappedFindAppsFiltersList = withFindAppsTracking(FiltersList<IAppsAndroidFiltersDto>);

const FindAppsAndroidFiltersContainer = () => {
    const dispatch = useDispatch();

    const isFilterPanelExpanded = useSelector(selectIsFiltersPanelExpanded);
    const filtersValue: Partial<IAppsAndroidFiltersDto> = useSelector(selectFindAppsAndroidFiltersValue);
    const expandedFilter: string | null = useSelector(selectFindAppsAndroidExpandedFilter);

    const { handleFilterUpdate, handleExpandFilter } = useUpdateTabState<IAppsAndroidFiltersDto>(
        AppsSourceType.GoogleStore,
    );

    const { data: description, isFetching } = useFindAppsDescriptionQuery();

    const filtersConfig = useMemo(() => createAndroidFilterConfig(description), [description]);

    const filtersManager = useFiltersManager<IAppsAndroidFiltersDto>({
        filtersValue,
        filtersConfig,
        onFiltersUpdate: handleFilterUpdate,
    });

    const handleFilterPanelToggle = useCallback((isExpanded: boolean) => {
        dispatch(setIsFiltersPanelExpanded(isExpanded));
    }, []);

    return (
        <FilterPanelContentContainer<IAppsAndroidFiltersDto>
            isExpanded={isFilterPanelExpanded}
            filtersManager={filtersManager}
            onPanelToggle={handleFilterPanelToggle}
        >
            <WrappedFindAppsFiltersList
                isLoading={isFetching}
                trackingTab={AppsSourceType.GoogleStore}
                filtersManager={filtersManager}
                expandedFilter={expandedFilter}
                onToggleExpand={(filterName) => {
                    handleExpandFilter(String(filterName) === expandedFilter ? null : String(filterName));
                }}
                onClearFilter={(key) => filtersManager.resetFilter(key)}
                onFilterUpdate={(key, value) => filtersManager.updateFilter(key, value)}
            />
        </FilterPanelContentContainer>
    );
};

export default memo(FindAppsAndroidFiltersContainer);
